import React from "react";
import { Compass, Target, Shield, MapPin, Rocket, Sparkles } from "lucide-react";

const STAFF = [
  {
    name: "Reginald \"Nunee\" Johnson",
    title: "Athletic Director",
    url: "https://customer-assets-7cd3h4nn.emergentagent.net/job_parish-united/artifacts/zuehgjjb_nunee%202.png",
  },
  {
    name: "Shelby \"Duke\" Boyle",
    title: "President",
    url: "https://customer-assets-7cd3h4nn.emergentagent.net/job_parish-united/artifacts/c7aiyrlt_duke.jpg",
  },
  {
    name: "Lloyd Richard",
    title: "Executive Board",
    url: "https://customer-assets-7cd3h4nn.emergentagent.net/job_parish-united/artifacts/b7ezlzl8_lloyd.jpg",
  },
  {
    name: "Ivory Washington",
    title: "Executive Board",
    url: "https://customer-assets-7cd3h4nn.emergentagent.net/job_parish-united/artifacts/i0i0zpcd_ivory.jpg",
  },
  {
    name: "Blake Prince",
    title: "Executive Board",
    url: "https://customer-assets-7cd3h4nn.emergentagent.net/job_parish-united/artifacts/dxccf13n_blake%20prince.jpg",
  },
  {
    name: "Antione Evans",
    title: "Executive Board",
    url: "https://customer-assets-7cd3h4nn.emergentagent.net/job_parish-united/artifacts/vk8hl8y7_Twan.jpg",
  },
  {
    name: "Randall Cancienne",
    title: "Executive Board / Business Operations",
    url: "https://customer-assets-7cd3h4nn.emergentagent.net/job_parish-united/artifacts/6mwd9js4_Randall_Cancienne_SCU.jpg",
  },
];

const pillars = [
  {
    icon: Target, label: "Mission",
    body: "Develop a competitive travel football and cheer program capable of competing across Louisiana and surrounding areas — while showcasing the deep athletic talent that has come from St. Charles Parish for generations.",
  },
  {
    icon: Compass, label: "Vision",
    body: "One program where athletes and families from the East Bank and West Bank can unite under one name and compete as one community — proudly representing where they come from.",
  },
  {
    icon: Shield, label: "Values",
    body: "Discipline, academics, accountability, teamwork, leadership, respect and hard work — building not only better athletes, but confident and responsible young people prepared for what comes next.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="bg-scu-obsidian text-white">
        <div className="mx-auto max-w-[1400px] px-4 lg:px-8 py-20 md:py-28">
          <div className="text-xs uppercase tracking-[0.35em] text-scu-electric">About SCU</div>
          <h1 className="mt-4 font-bebas text-6xl md:text-8xl tracking-wider">Two Banks.<br />One Parish.<br />One United Family.</h1>
          <p className="mt-3 text-xs uppercase tracking-[0.35em] text-scu-electric font-bold">U · T · E · S · United Together. Empowering St. Charles.</p>
        </div>
        <div className="stripe-divider" />
      </section>

      {/* ABOUT BODY */}
      <section className="mx-auto max-w-4xl px-4 lg:px-8 py-20 space-y-6 text-scu-smoke text-lg leading-relaxed">
        <p>
          St. Charles United was built to represent all of St. Charles Parish. Our identity brings together the traditions
          of both sides of the river, with <b className="text-scu-purple">Hahnville's purple</b> and{" "}
          <b className="text-scu-obsidian">Destrehan's smoke grey</b> forming the foundation of the SCU colors.
          Together, they symbolize our goal: creating one program where athletes and families from the East Bank
          and West Bank can unite under one name and compete as one community.
        </p>
        <p>
          Our mascot, the <b className="text-scu-obsidian">Ute</b>, represents that identity in two ways. It draws
          inspiration from the Ute Native people — a name historically associated with strength, resilience and
          community. It also represents what SCU stands for:{" "}
          <b className="text-scu-purple">U.T.E.S. — United Together, Empowering St. Charles.</b> It is more than a team
          name; it represents our commitment to bringing our parish together and building something our young athletes
          can be proud to represent.
        </p>
        <p>
          SCU was created to develop a competitive travel football and cheer program capable of competing with strong
          programs throughout Louisiana and surrounding areas while showcasing the deep athletic talent that has come
          from St. Charles Parish for generations. We want our athletes to have opportunities to compete at a high level,
          grow their abilities and proudly represent where they come from.
        </p>
        <p>
          But our mission extends beyond wins and losses. Success at SCU means success on the field, in the classroom,
          and in life. We will challenge our young athletes to value discipline, academics, accountability, teamwork,
          leadership, respect and hard work. Our goal is to help develop not only better athletes, but confident and
          responsible young people prepared for whatever comes next.
        </p>
        <p className="pt-2 font-bebas text-3xl md:text-4xl tracking-wide text-scu-obsidian text-center">
          Two Banks. One Parish. One United Family.
        </p>
      </section>

      {/* PARISH COLOR IDENTITY STRIP */}
      <section className="mx-auto max-w-[1400px] px-4 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-scu-purple text-white rounded-md p-6 flex items-center gap-4">
            <MapPin className="h-6 w-6 text-white flex-shrink-0" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.35em] text-white/70 font-bold">West Bank</div>
              <div className="font-bebas text-3xl tracking-wide">Hahnville Purple</div>
            </div>
          </div>
          <div className="bg-scu-smoke text-white rounded-md p-6 flex items-center gap-4">
            <MapPin className="h-6 w-6 text-white flex-shrink-0" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.35em] text-white/70 font-bold">East Bank</div>
              <div className="font-bebas text-3xl tracking-wide">Destrehan Smoke Grey</div>
            </div>
          </div>
        </div>
      </section>

      {/* SCU HISTORY MILESTONES */}
      <section className="mx-auto max-w-[1400px] px-4 lg:px-8 py-20">
        <div className="text-xs uppercase tracking-[0.35em] text-scu-purple font-bold">SCU History</div>
        <h2 className="mt-3 font-bebas text-5xl md:text-6xl tracking-wide text-scu-obsidian">Milestones</h2>

        <div className="mt-10 grid md:grid-cols-2 gap-5">
          <div className="relative overflow-hidden rounded-md bg-scu-obsidian text-white p-8 md:p-10 border border-scu-purple/40">
            <div className="scanlines absolute inset-0 opacity-20" />
            <div className="relative">
              <div className="flex items-center gap-3">
                <Sparkles className="h-6 w-6 text-scu-electric" />
                <div className="text-[10px] uppercase tracking-[0.35em] text-scu-electric font-bold">Established</div>
              </div>
              <div className="mt-4 font-bebas text-7xl md:text-8xl tracking-wide leading-none text-white">2026</div>
              <div className="mt-3 text-white/80">SCU was formed and organized to represent all of St. Charles Parish — East Bank and West Bank, united under one name.</div>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-md bg-scu-purple text-white p-8 md:p-10 scu-purple-glow">
            <div className="scanlines absolute inset-0 opacity-30" />
            <div className="relative">
              <div className="flex items-center gap-3">
                <Rocket className="h-6 w-6 text-white" />
                <div className="text-[10px] uppercase tracking-[0.35em] text-white/80 font-bold">Inaugural Season</div>
              </div>
              <div className="mt-4 font-bebas text-7xl md:text-8xl tracking-wide leading-none text-white">2027</div>
              <div className="mt-3 text-white/90">The St. Charles United Utes officially begin competition in youth travel football and cheer.</div>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION / VISION / VALUES PILLARS */}
      <section className="mx-auto max-w-[1400px] px-4 lg:px-8 pb-4">
        <div className="grid md:grid-cols-3 gap-5">
          {pillars.map((p) => (
            <div key={p.label} className="border-l-4 border-scu-purple bg-white rounded-md p-6">
              <div className="flex items-center gap-2 text-scu-purple">
                <p.icon className="h-5 w-5" />
                <div className="text-xs uppercase tracking-[0.3em] font-bold">{p.label}</div>
              </div>
              <h3 className="mt-2 font-bebas text-3xl tracking-wide text-scu-obsidian">Our {p.label}</h3>
              <p className="mt-2 text-scu-smoke leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* LEADERSHIP */}
      <section className="bg-scu-smoke text-white py-20 mt-20">
        <div className="mx-auto max-w-[1400px] px-4 lg:px-8">
          <div className="text-xs uppercase tracking-[0.35em] text-scu-electric">Leadership</div>
          <h2 className="mt-3 font-bebas text-5xl md:text-6xl tracking-wide">The Team Behind SCU</h2>
          <p className="mt-3 text-white/70 max-w-2xl">The people who help build St. Charles United from the ground up.</p>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-7 gap-4" data-testid="leadership-grid">
            {STAFF.map((s) => (
              <div key={s.name} className="bg-white/5 border border-white/10 backdrop-blur-md rounded-md overflow-hidden hover:border-scu-electric transition-colors" data-testid={`staff-${s.name.split(" ")[0].toLowerCase()}`}>
                <div className="aspect-[3/4] w-full overflow-hidden bg-scu-obsidian">
                  <img
                    src={s.url}
                    alt={s.name}
                    loading="lazy"
                    className="h-full w-full object-cover"
                    style={{ objectPosition: "center 20%" }}
                  />
                </div>
                <div className="p-4 text-center">
                  <div className="font-bebas text-xl md:text-2xl tracking-wide leading-tight">{s.name}</div>
                  {s.title && <div className="mt-1 text-[10px] uppercase tracking-[0.25em] text-white font-bold">{s.title}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
